import * as React from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { Row, Col } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { Field } from 'redux-form';

import type { EmployeeOptionVM } from 'ab-viewModels/employee/extendedOption.viewModel';
import type EquipmentViewModel from 'ab-viewModels/equipment.viewModel';
import type TemporaryEmployeeOptionVM from 'ab-viewModels/temporaryEmployee/temporaryEmployeeOption.viewModel';

import { isAllowed } from '@acceligentllc/shared/utils/auth';

import type { AssignableResourceType } from '@acceligentllc/shared/enums/assignableResourceType';

import type { RootState } from 'af-reducers';

import PagePermissions from 'ab-enums/pagePermissions.enum';

import Checkbox from 'af-fields/Checkbox';
import Input from 'af-fields/Input';

import RemoveResourceLookupButton from './RemoveResourceLookupButton';
import ResourceLookupItem from './ResourceLookupItem';
import type WorkOrderFM from '../../formModel';

interface OwnProps {
	disabled?: boolean;
	draggableId: string;
	dueDate: string;
	employees: EmployeeOptionVM[];
	equipment: EquipmentViewModel[];
	temporaryEmployees: TemporaryEmployeeOptionVM[];
	index: number;
	isAllowedToManagePerDiems: boolean;
	isCancelled: boolean;
	loadEmployees: (isLazyLoaded: boolean) => void;
	loadEquipment: (isLazyLoaded: boolean) => void;
	loadTemporaryEmployees: (isLazyLoaded: boolean) => void;
	removeWorkOrderResourceLookup: (index: number) => void;
	resetTimer: () => void;
	resourceLookup: WorkOrderFM['workOrderResourceLookups'][0];
	resourceLookupPropName: string;
	showResourceAssignConfirmationModal: (resourceType: AssignableResourceType) => void;
	workOrderId: number;
	updatePerDiemAmount: (resourceLookupPropName: string, index: number, perDiemCheck: boolean) => void;
	foremanEnabled: boolean;
	updateForeman: (index: number, isCrewLead: boolean) => void;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const DraggableResourceLookup: React.FC<Props> = (props: Props) => {
	const {
		disabled,
		draggableId,
		dueDate,
		employees,
		equipment,
		temporaryEmployees,
		index,
		isAllowedToManagePerDiems,
		loadEmployees,
		loadEquipment,
		loadTemporaryEmployees,
		removeWorkOrderResourceLookup,
		resetTimer,
		resourceLookup,
		resourceLookupPropName,
		isCancelled,
		showResourceAssignConfirmationModal,
		updatePerDiemAmount,
		foremanEnabled,
		updateForeman,
		canManageForeman,
	} = props;

	const onPerDiemCheck = React.useCallback((value: boolean) => {
		updatePerDiemAmount(resourceLookupPropName, index, value);
	}, [index, resourceLookupPropName, updatePerDiemAmount]);

	const onForemanCheck = React.useCallback((value: boolean) => {
		updateForeman(index, value);
	}, [index, updateForeman]);

	return (
		<Draggable
			draggableId={draggableId}
			index={index}
			isDragDisabled={disabled}
		>
			{(draggableProvided) => (
				<div
					ref={draggableProvided.innerRef}
					{...draggableProvided.draggableProps}
					{...draggableProvided.dragHandleProps}
					className="resource-lookup__draggable"
					id={`accqa__wo-edit__resource__${index}`}
				>
					<Row className="row--non-padded">
						<Col className="resource-lookup__draggable--details" sm={12}>
							{!disabled && <span className="icon-drag_indicator" />}
							<ResourceLookupItem
								disabled={!!disabled}
								dueDate={dueDate}
								employees={employees}
								equipment={equipment}
								index={index}
								loadEmployees={loadEmployees}
								loadEquipment={loadEquipment}
								loadTemporaryEmployees={loadTemporaryEmployees}
								resetTimer={resetTimer}
								resourceLookup={resourceLookup}
								resourceLookupPropName={resourceLookupPropName}
								showResourceAssignConfirmationModal={showResourceAssignConfirmationModal}
								temporaryEmployees={temporaryEmployees}
							/>
							<RemoveResourceLookupButton
								disabled={!!disabled}
								index={index}
								removeWorkOrderResourceLookup={removeWorkOrderResourceLookup}
							/>
						</Col>
						{resourceLookup.employeeId && isAllowedToManagePerDiems &&
							<Col sm={3} xs={6}>
								<Field
									component={Checkbox}
									extraClass="resource-lookup__per-diem"
									isDisabled={disabled && !isCancelled}
									isStandalone={true}
									label="Per Diem"
									name={`${resourceLookupPropName}.perDiem`}
									onValueChange={onPerDiemCheck}
								/>
							</Col>
						}
						{resourceLookup.employeeId && resourceLookup.perDiem && isAllowedToManagePerDiems &&
							<Col className="resource-lookup__per-diem-amount" sm={5} xs={18}>
								<Field
									addonAfter="$"
									className="resource-lookup__per-diem-amount-input"
									component={Input}
									disabled={disabled && !isCancelled}
									isStandalone={true}
									isZeroIfEmpty={true}
									name={`${resourceLookupPropName}.perDiemAmount`}
									onValueChange={resetTimer}
									type="number"
									withThousandsSeparator={true}
								/>
								<div className="resource-lookup__per-diem-amount-label">Amount</div>
							</Col>
						}
						{resourceLookup.employeeId && foremanEnabled && canManageForeman &&
							<Col sm={3} xs={6}>
								<Field
									component={Checkbox}
									extraClass="resource-lookup__foreman"
									isDisabled={disabled}
									isStandalone={true}
									label="Crew Lead"
									name={`${resourceLookupPropName}.isCrewLead`}
									onValueChange={onForemanCheck}
								/>
							</Col>
						}
					</Row>
				</div>
			)}
		</Draggable>
	);
};

function mapStateToProps(state: RootState) {
	const { user: { companyData, userData } } = state;

	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const { isCompanyAdmin, permissions } = companyData;
	const { role } = userData;

	const canManageForeman = isAllowed(
		PagePermissions.COMPANY.FOREMAN.MANAGE,
		permissions,
		isCompanyAdmin,
		role
	);

	return {
		canManageForeman,
	};
}

const connector = connect(mapStateToProps);

export default connector(DraggableResourceLookup);
